import type { FareBreakdown, Place, VehicleType } from '@raahi/shared';
import { request } from './client.js';

export interface FareEstimate {
  distanceKm: number;
  durationMin: number;
  surgeMultiplier: number;
  fare: FareBreakdown;
}

export interface TripDto {
  id: string;
  status: string;
  vehicleType: VehicleType;
  pickup: Place;
  drop: Place;
  distanceKm: number;
  durationMin: number;
  fare: FareBreakdown;
  paymentMethod: string;
  requestedAt: string;
  assignedAt: string | null;
  arrivedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  driver?: {
    id: string;
    name: string;
    rating: number;
    vehicle?: { make: string; model: string; plate: string; color: string; type: VehicleType };
  };
  events?: Array<{ type: string; at: string }>;
}

export function estimateFare(input: {
  type: VehicleType;
  pickup: { lat: number; lng: number };
  drop: { lat: number; lng: number };
}): Promise<FareEstimate> {
  return request<FareEstimate>('/pricing/estimate', {
    query: {
      type: input.type,
      pickupLat: input.pickup.lat,
      pickupLng: input.pickup.lng,
      dropLat: input.drop.lat,
      dropLng: input.drop.lng,
    },
  });
}

export function nearbyDrivers(input: {
  lat: number;
  lng: number;
  type?: VehicleType;
}): Promise<Array<{ id: string; lat: number; lng: number; heading: number; vehicleType: VehicleType }>> {
  return request('/drivers/nearby', { query: { lat: input.lat, lng: input.lng, type: input.type } });
}

export function requestRide(input: {
  vehicleType: VehicleType;
  pickup: Place;
  drop: Place;
  paymentMethod: 'cash' | 'card';
}): Promise<TripDto> {
  return request<TripDto>('/trips', { method: 'POST', body: input });
}

export function getTrip(id: string): Promise<TripDto> {
  return request<TripDto>(`/trips/${id}`);
}

export function cancelTrip(id: string, reason?: string): Promise<TripDto> {
  return request<TripDto>(`/trips/${id}/cancel`, { method: 'POST', body: { reason } });
}

export function rateTrip(id: string, rating: number, note?: string): Promise<TripDto> {
  return request<TripDto>(`/trips/${id}/rate`, { method: 'POST', body: { rating, note } });
}

export function tripHistory(): Promise<TripDto[]> {
  return request<TripDto[]>('/trips/history');
}
